<template>
  <div>
    <div class="vx-col sm:w-1/1 w-full mb-6">
      <ValidationObserver
        v-slot="{ invalid }"
        v-for="item in antiFraudConfigs"
        :key="item.id"
      >
        <vx-card class="mb-8">
          <div>
            <div class="flex flex-row items-center justify-between">
              <h3>{{ (item.providerName || '').toUpperCase() }}</h3>

              <vs-button
                radius
                v-tooltip="'Histórico de Alterações'"
                color="#88abc2"
                icon-pack="feather"
                icon="icon-archive"
                class="d-inline"
                :data-testId="`anti-fraud-history-button-${item.id}`"
                @click="openHistoryModal(item.id)"
              ></vs-button>
            </div>

            <div class="vx-row mb-6">
              <div class="vx-col w-1/6 flex items-center">
                <span class="mr-3">Status</span>
                <vs-switch
                  v-model="item.active"
                  color="success"
                  :disabled="!canWrite"
                  :data-testId="`anti-fraud-status-switch-${item.id}`"
                />
              </div>

              <div class="vx-col w-1/6">
                <span>URL Base</span>
                <ValidationProvider
                  name="URL Base"
                  rules="required|url"
                  v-slot="{ errors }"
                >
                  <vs-input
                    v-model="item.baseUrl"
                    class="w-full"
                    :class="{ 'error-border': errors.length }"
                    :disabled="!canWrite"
                    :data-testId="`anti-fraud-base-url-input-${item.id}`"
                  />
                  <span class="error-text">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>

              <div class="vx-col w-1/6">
                <span>Tipo de Autenticação</span>
                <vs-select
                  class="w-full"
                  v-model="item.authenticationType"
                  :disabled="!canWrite"
                  :data-testId="`anti-fraud-auth-type-select-${item.id}`"
                >
                  <vs-select-item value="auth" text="Usuário e Senha" />
                  <vs-select-item value="key" text="API Key" />
                </vs-select>
              </div>
            </div>

            <div class="vx-row mb-12">
              <template v-if="item.authenticationType === 'auth'">
                <div class="vx-col w-1/6">
                  <span>Usuário PF</span>
                  <ValidationProvider
                    v-if="item.authenticationType === 'auth'"
                    name="Usuário PF"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <vs-input
                      v-model="item.username"
                      class="w-full"
                      :class="{ 'error-border': errors.length }"
                      :disabled="!canWrite"
                      :data-testId="`anti-fraud-username-input-${item.id}`"
                    />
                    <span class="error-text">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>

                <div class="vx-col w-1/6">
                  <span>Senha PF</span>
                  <ValidationProvider
                    name="Secret"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <vs-input
                      v-model="item.secret"
                      class="w-full"
                      :class="{ 'error-border': errors.length }"
                      :disabled="!canWrite"
                      :data-testId="`anti-fraud-secret-input-${item.id}`"
                    />
                    <span class="error-text">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>

                <div class="vx-col w-1/6">
                  <span>Usuário PJ</span>
                  <vs-input
                    v-model="item.usernameAlt"
                    class="w-full"
                    :disabled="!canWrite"
                    :data-testId="`anti-fraud-username-alt-input-${item.id}`"
                  />
                </div>

                <div class="vx-col w-1/6">
                  <span>Senha PJ</span>
                  <vs-input
                    v-model="item.secretAlt"
                    class="w-full"
                    :disabled="!canWrite"
                    :data-testId="`anti-fraud-secret-alt-input-${item.id}`"
                  />
                </div>
              </template>

              <template v-else-if="item.authenticationType === 'key'">
                <div class="vx-col w-1/6">
                  <span>API Key</span>
                  <ValidationProvider
                    name="Secret"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <vs-input
                      v-model="item.secret"
                      class="w-full"
                      :class="{ 'error-border': errors.length }"
                      :disabled="!canWrite"
                      :data-testId="`anti-fraud-api-key-input-${item.id}`"
                    />
                    <span class="error-text">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </template>
            </div>

            <h4 class="mb-4">Produtos</h4>
            <div class="vx-row mb-12">
              <div
                class="vx-col w-1/6 flex items-center"
                v-for="(product, productKey) in item.products"
                :key="productKey"
              >
                <span class="mr-3">{{ formatLabel(productKey) }}</span>
                <vs-switch
                  v-model="product.active"
                  color="success"
                  :disabled="!canWrite"
                  :data-testId="`anti-fraud-product-switch-${item.id}-${productKey}`"
                />
              </div>
            </div>

            <div
              v-for="[productKey, product] in productsWithRules(item.products)"
              :key="productKey"
            >
              <h4 class="mb-6">
                {{ formatLabel(productKey) }}
              </h4>

              <div class="vx-row mb-4">
                <div
                  class="vx-col w-1/3"
                  v-for="(ruleGroup, ruleGroupKey) in product.rules"
                  :key="ruleGroupKey"
                >
                  <div class="flex flex-row items-center justify-between mb-4">
                    <h5 class="mb-0">
                      {{ formatProductName(ruleGroupKey) }}
                    </h5>
                    <div
                      class="flex items-center"
                      v-if="ruleGroupKey === 'newPolicyAndEndorsement'"
                    >
                      <span class="mr-2">Análise manual</span>
                      <vs-switch
                        color="success"
                        :value="!!ruleGroup.manualReview"
                        :disabled="!canWrite"
                        @input="toggleManualReview(ruleGroup, $event)"
                      />
                    </div>
                  </div>

                  <div
                    class="vx-row mb-4 flex-row items-center"
                    v-for="[decisionKey, decision] in sortedRuleGroup(
                      ruleGroup,
                    )"
                    :key="decisionKey"
                  >
                    <div class="vx-col w-2/5">
                      <span>{{ formatDecision(decisionKey) }}</span>
                    </div>

                    <div class="vx-col w-3/12">
                      <vs-input
                        type="number"
                        min="0"
                        max="1000"
                        class="w-full"
                        v-model.number="decision.from"
                        :placeholder="decision.from === 0 ? '' : 'De'"
                        :disabled="!canWrite"
                        :data-testId="`anti-fraud-rule-from-${productKey}-${ruleGroupKey}-${decisionKey}`"
                      />
                    </div>

                    <div class="vx-col w-3/12">
                      <vs-input
                        type="number"
                        min="0"
                        max="1000"
                        class="w-full"
                        v-model.number="decision.to"
                        :placeholder="decision.from === 0 ? '' : 'Até'"
                        :disabled="!canWrite"
                        :data-testId="`anti-fraud-rule-to-${productKey}-${ruleGroupKey}-${decisionKey}`"
                      />
                    </div>
                  </div>

                  <vs-alert
                    v-if="(ruleGroupError = validateRuleGroup(ruleGroup))"
                    color="danger"
                    class="mt-2"
                  >
                    {{ ruleGroupError }}
                  </vs-alert>
                </div>
              </div>
            </div>

            <div class="w-full flex justify-end">
              <vs-button
                v-if="canWrite"
                color="success"
                :disabled="invalid || hasProductRulesErrors(item)"
                :data-testId="`anti-fraud-save-button-${item.id}`"
                @click="updateConfigs(item.id, item)"
              >
                Salvar Alterações
              </vs-button>
            </div>
          </div>
        </vx-card>
      </ValidationObserver>
    </div>

    <AntiFraudHistoryModal
      :isOpen="isHistoryModalOpen"
      :antiFraudConfigId="selectedAntiFraudConfigId"
      @close="isHistoryModalOpen = false"
    />
  </div>
</template>

<script>
import locale from '../../../locale';
import notify from '../../../utils/notify';
import antiFraudApiService from '../../../services/anti.fraud.api';
import AntiFraudHistoryModal from './components/AntiFraudConfigHistoryModal.vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

export default {
  components: { AntiFraudHistoryModal, ValidationObserver, ValidationProvider },
  data() {
    return {
      isHistoryModalOpen: false,
      selectedAntiFraudConfigId: null,
      antiFraudConfigs: [],
    };
  },
  computed: {
    canWrite() {
      return this.$can(this.$actionsDefault.Escrita, 'Antifraude');
    },
    productsWithRules() {
      return (products) =>
        Object.entries(products || {}).filter(
          ([, product]) => product.active && product.rules,
        );
    },
  },
  methods: {
    async getAllConfigs() {
      this.$vs.loading();
      try {
        const data = await antiFraudApiService.getAllConfigs();

        this.antiFraudConfigs = data;
      } catch {
        notify.notifyFail(
          this.$vs,
          'Erro ao buscar configurações de antifraude',
        );
      } finally {
        this.$vs.loading.close();
      }
    },
    openHistoryModal(antiFraudConfigId) {
      this.selectedAntiFraudConfigId = antiFraudConfigId;
      this.isHistoryModalOpen = true;
    },

    validateRuleGroup(ruleGroup) {
      const decisions = Object.values(ruleGroup).sort(
        (a, b) => a.from - b.from,
      );

      for (let i = 0; i < decisions.length; i++) {
        const current = decisions[i];
        const prev = decisions[i - 1];

        if (current.from == null || current.to == null) {
          return 'Todos os campos devem ser preenchidos';
        }

        if (current.from < 0 || current.to > 1000) {
          return 'Os valores devem estar entre 0 e 1000';
        }

        if (current.from > current.to) {
          return 'O valor "De" não pode ser maior que "Até"';
        }

        if (i === 0 && current.from !== 0) {
          return 'A primeira regra deve iniciar em 0';
        }

        if (prev && current.from !== prev.to + 1) {
          return `A escala deve ser contínua. Esperado ${prev.to + 1}`;
        }
      }

      const last = decisions[decisions.length - 1];
      if (last.to !== 1000) {
        return 'A última regra deve terminar em 1000';
      }

      return null;
    },

    hasProductRulesErrors(item) {
      for (const [, product] of Object.entries(item.products || {})) {
        if (!product.active || !product.rules) continue;

        for (const ruleGroup of Object.values(product.rules)) {
          if (this.validateRuleGroup(ruleGroup)) {
            return true;
          }
        }
      }
      return false;
    },

    async updateConfigs(id, data) {
      if (this.hasProductRulesErrors(data)) {
        notify.notifyFail(this.$vs, 'Existem erros nas regras');
        return;
      }

      this.$vs.loading();
      try {
        await antiFraudApiService.updateConfigs(id, data);

        notify.notifySuccess(this.$vs, 'Dados atualizados!');

        this.getAllConfigs();
      } catch {
        notify.notifyFail(
          this.$vs,
          'Erro ao atualizar configurações de antifraude',
        );
      } finally {
        this.$vs.loading.close();
      }
    },

    formatLabel(key) {
      return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase());
    },

    formatProductName(key) {
      const map = {
        newPolicyAndEndorsement: 'Geral',
        oneTimePayment: 'Pagamento Avulso',
      };
      return map[key] || this.formatLabel(key);
    },

    toggleManualReview(ruleGroup, enabled) {
      if (enabled) {
        if (!ruleGroup.manualReview) {
          this.$set(ruleGroup, 'manualReview', { from: null, to: null });
        }
      } else {
        this.$delete(ruleGroup, 'manualReview');
      }
    },

    sortedRuleGroup(ruleGroup) {
      const order = ['autoReject', 'manualReview', 'autoApprove'];
      return Object.entries(ruleGroup).sort(
        ([a], [b]) => order.indexOf(a) - order.indexOf(b),
      );
    },

    formatDecision(key) {
      const map = {
        autoApprove: 'Aprovação automática',
        manualReview: 'Análise manual',
        autoReject: 'Recusa automática',
      };
      return map[key] || this.formatLabel(key);
    },
  },

  mounted() {
    this.$validator.localize('ptBr', locale);
    this.getAllConfigs();
  },
};
</script>

<style scoped lang="scss">
.error-text {
  color: red;
  font-size: 12px;
  margin-top: 4px;
}
</style>
